import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import {
    Alert, Button, Col, DatePicker, Empty, Input, InputNumber, Modal, Pagination, Row, Segmented, Select, Space, Tabs,
    Tag, Tooltip, message
} from 'antd'
import {
    MailOutlined, PhoneOutlined, CopyOutlined, UserOutlined, IdcardOutlined, CalendarOutlined, NumberOutlined,
    LockOutlined, KeyOutlined, TagOutlined, ShoppingCartOutlined, ShoppingOutlined, WalletOutlined, LineChartOutlined,
    GiftOutlined, NotificationOutlined, StopOutlined, UnlockOutlined, PlusOutlined, MinusOutlined,
    ArrowUpOutlined, ArrowDownOutlined, FireOutlined, ProfileOutlined, StarFilled, UserAddOutlined, HourglassOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import i from '../../translations/i'
import { Titles } from '../../layouts/Titles'
import { AdditionalFields } from '../../components/AdditionalFields'
import { storeLocal } from '../../helpers/stores'
import { openApi } from '../../helpers/request'
import { getPrice } from '../../helpers/price'
import { dayToUtcTimestamp, utcTimestampToDay } from '../../helpers/timestamp/datetime.helper'
import projectTime from '../../helpers/project_time'
import price_types_controller from '../settings/price_types/controller'
import { isAdminRole } from '../marketing/carts/carts.helpers'
import { RatingStars, entityLink, statusColor, typeIcon } from '../shop/reviews/Reviews'
import { REVIEW_PLANS, planAllows } from '../settings/reviews/reviewPlans'
import __users from './users.controller'
import './user.less'

dayjs.extend(utc)

const copy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(i.t('order:copied'))
}

// Сколько дней до ближайшего дня рождения; birthday хранится как unix-время начала дня по времени проекта
const daysToBirthday = (birthday: number) => {
    if (!birthday) return null

    const today = projectTime.wall(dayjs().unix()).startOf('day')
    let next = projectTime.wall(birthday).year(today.year()).startOf('day')
    if (next.isBefore(today)) next = next.add(1, 'year')

    return next.diff(today, 'day')
}

const Card = ({ icon, title, hint, extra, children }: {
    icon: React.ReactNode
    title: React.ReactNode
    hint?: React.ReactNode
    extra?: React.ReactNode
    children: React.ReactNode
}) => (
    <section className="user-card">
        <div className="user-card__head">
            <div className="user-card__title">
                <span className="user-card__icon">{icon}</span>
                <div>
                    {title}
                    {hint && <div className="user-card__hint">{hint}</div>}
                </div>
            </div>
            {extra}
        </div>
        {children}
    </section>
)

const Field = ({ icon, label, children }: { icon?: React.ReactNode, label: React.ReactNode, children: React.ReactNode }) => (
    <div className="user-field">
        <span className="user-field__label">{icon}{label}</span>
        {children}
    </div>
)

const Stat = ({ tone, icon, label, value, sub }: {
    tone: 'violet' | 'blue' | 'green' | 'orange'
    icon: React.ReactNode
    label: React.ReactNode
    value: React.ReactNode
    sub?: React.ReactNode
}) => (
    <div className={`user-stat user-stat--${tone}`}>
        <div className="user-stat__icon">{icon}</div>
        <div className="user-stat__value">{value}</div>
        <div className="user-stat__label">{label}</div>
        {sub && <div className="user-stat__sub">{sub}</div>}
    </div>
)

const ContactPill = ({ icon, value, href, empty }: { icon: React.ReactNode, value: string, href: string, empty: string }) => value
    ? (
        <span className="user-pill">
            <a href={href} className="user-pill__link">{icon}<span>{value}</span></a>
            <Tooltip title={i.t('order:clickToCopy')}>
                <button type="button" className="user-pill__copy" onClick={() => copy(value)}><CopyOutlined /></button>
            </Tooltip>
        </span>
    )
    : <span className="user-pill user-pill--empty">{icon}<span>{empty}</span></span>

const UserHero = observer(() => {
    const user: any = __users.user
    const isNew = !(user.id > 0)
    const name = [user.firstname, user.lastname, user.surname].filter(Boolean).join(' ')
    const initials = `${user.firstname?.[0] ?? ''}${user.lastname?.[0] ?? ''}`.toUpperCase()
    const birthdayIn = daysToBirthday(user.birthday)
    // Цвет аватара постоянный для каждого пользователя, чтобы их было проще различать
    const hue = (user.id * 137) % 360

    return (
        <section className="user-hero">
            <div className="user-hero__cover" />
            <div className="user-hero__body">
                <div
                    className="user-hero__avatar"
                    style={{ background: isNew ? undefined : `linear-gradient(135deg, hsl(${hue}, 75%, 62%), hsl(${hue + 35}, 70%, 48%))` }}
                >
                    {isNew ? <UserAddOutlined /> : (initials || <UserOutlined />)}
                </div>

                <div className="user-hero__info">
                    <div className="user-hero__name-row">
                        <h2 className="user-hero__name">{isNew ? i.t('users:profile.newUser') : (name || `#${user.id}`)}</h2>
                        {!isNew && (
                            user.deleted_at
                                ? <span className="user-badge user-badge--danger">{i.t('users:profile.deleted')}</span>
                                : user.is_blocked
                                    ? <span className="user-badge user-badge--danger"><StopOutlined />{i.t('users:profile.blocked')}</span>
                                    : <span className="user-badge user-badge--success"><span className="user-badge__dot" />{i.t('users:profile.active')}</span>
                        )}
                    </div>

                    {!isNew && <>
                        <div className="user-hero__meta">
                            <span>#{user.id}</span>
                            {user.created_at && <span>{i.t('users:profile.clientSince', { date: dayjs(user.created_at).format('DD.MM.YYYY') })}</span>}
                        </div>

                        <div className="user-hero__contacts">
                            <ContactPill icon={<MailOutlined />} value={user.email} href={`mailto:${user.email}`} empty={i.t('users:profile.noEmail')} />
                            <ContactPill icon={<PhoneOutlined />} value={user.phone} href={`tel:${user.phone}`} empty={i.t('users:profile.noPhone')} />
                            {birthdayIn !== null && birthdayIn <= 30 &&
                                <span className="user-pill user-pill--birthday">
                                    <GiftOutlined />
                                    <span>{birthdayIn === 0 ? i.t('users:profile.birthdayToday') : i.t('users:profile.birthdayIn', { count: birthdayIn })}</span>
                                </span>
                            }
                        </div>
                    </>}
                </div>

                {!isNew &&
                    <div className="user-hero__actions">
                        <Button icon={<ShoppingOutlined />} href={`/shop/orders?user_id=${user.id}`} target="_blank">{i.t('users:goToOrders')}</Button>
                        <Button icon={<NotificationOutlined />} href={`/pushes/0?userId=${user.id}`}>{i.t('users:sendPush')}</Button>
                        {/* Корзины пользователей смотрит и правит только администратор */}
                        {isAdminRole() &&
                            <Button icon={<ShoppingCartOutlined />} href={`/carts/user/${user.id}`}>{i.t('carts:user.openCart')}</Button>
                        }
                        <Button
                            danger={!user.is_blocked}
                            icon={user.is_blocked ? <UnlockOutlined /> : <StopOutlined />}
                            onClick={() => __users.blockUser(user.id)}
                        >
                            {user.is_blocked ? i.t('def:actions.userUnBlock') : i.t('def:actions.userBlock')}
                        </Button>
                    </div>
                }
            </div>
        </section>
    )
})

const UserStats = observer(() => {
    const orders = __users.user.orders ?? {}
    const count = orders.count ?? 0
    const average = count > 0 ? (orders.sum ?? 0) / count : 0

    return (
        <div className="user-stats">
            <Stat
                tone="violet"
                icon={<ShoppingOutlined />}
                label={i.t('users:profile.orders')}
                value={count}
                sub={i.t('users:profile.paidCount', { count: orders.count_pay ?? 0 })}
            />
            <Stat
                tone="blue"
                icon={<WalletOutlined />}
                label={i.t('users:profile.ordersSum')}
                value={getPrice(orders.sum ?? 0)}
                sub={i.t('users:profile.paidSum', { sum: getPrice(orders.sum_pay ?? 0) })}
            />
            <Stat
                tone="green"
                icon={<LineChartOutlined />}
                label={i.t('users:profile.avgCheck')}
                value={getPrice(average)}
                sub={i.t('users:profile.avgCheckHint')}
            />
            <Stat
                tone="orange"
                icon={<GiftOutlined />}
                label={i.t('users:profile.bonusBalance')}
                value={__users.bonus_balance ?? 0}
                sub={i.t('users:profile.bonusUnit')}
            />
        </div>
    )
})

const PersonalCard = observer(() => {
    const user = __users.user

    return (
        <Card icon={<IdcardOutlined />} title={i.t('users:profile.personal')}>
            <div className="user-fields">
                <Field icon={<UserOutlined />} label={i.t('user:name')}>
                    <Input variant="filled" value={user.firstname} onChange={e => __users.user.firstname = e.target.value} />
                </Field>
                <Field label={i.t('users:surname')}>
                    <Input variant="filled" value={user.lastname} onChange={e => __users.user.lastname = e.target.value} />
                </Field>
                <Field label={i.t('user:middle')}>
                    <Input variant="filled" value={user.surname} onChange={e => __users.user.surname = e.target.value} />
                </Field>
                <Field icon={<CalendarOutlined />} label={i.t('user:birthday')}>
                    <DatePicker
                        variant="filled"
                        format="DD.MM.YYYY"
                        style={{ width: '100%' }}
                        value={user.birthday ? projectTime.wall(user.birthday) : null}
                        onChange={date => __users.user.birthday = date ? projectTime.startOfDay(date) : 0}
                    />
                </Field>
                <Field icon={<MailOutlined />} label="E-mail">
                    <Input variant="filled" value={user.email} onChange={e => __users.user.email = e.target.value} />
                </Field>
                <Field icon={<PhoneOutlined />} label={i.t('user:phone')}>
                    <Input variant="filled" value={user.phone} onChange={e => __users.user.phone = e.target.value} />
                </Field>
                <Field icon={<NumberOutlined />} label={i.t('def:externalId')}>
                    <Input variant="filled" value={user.external_id} onChange={e => __users.user.external_id = e.target.value} />
                </Field>
                <Field icon={<TagOutlined />} label={i.t('def:Price type')}>
                    <Select
                        variant="filled"
                        value={user.price_type_id ?? null}
                        onChange={e => __users.user.price_type_id = e}
                        options={[
                            { label: i.t('def:Select price type'), value: null },
                            ...price_types_controller.items.map((el: any) => ({
                                label: el.name ?? el.private_name,
                                value: el.id
                            }))
                        ]}
                        style={{ width: '100%' }}
                    />
                </Field>
            </div>
        </Card>
    )
})

const SecurityCard = observer(() => (
    <Card
        icon={<LockOutlined />}
        title={i.t('users:profile.security')}
        hint={__users.user.id > 0 && i.t('users:profile.securityHint')}
    >
        <div className="user-fields">
            <Field label={i.t('def:password_one')}>
                <Input.Password variant="filled" value={__users.password.password} onChange={e => __users.password.password = e.target.value} />
            </Field>
            <Field label={i.t('def:password_repeat')}>
                <Input.Password variant="filled" value={__users.password.password_repeat} onChange={e => __users.password.password_repeat = e.target.value} />
            </Field>
            <Field icon={<KeyOutlined />} label={i.t('def:Auth code')}>
                <Input
                    variant="filled"
                    maxLength={4}
                    value={__users.user.auth_code ?? ''}
                    onChange={e => {
                        if (/^\d{0,4}$/.test(e.target.value)) __users.user.auth_code = e.target.value
                    }}
                />
            </Field>
        </div>
    </Card>
))

const BonusHistoryItem = ({ item }: { item: any }) => {
    const kind = item.type === 'topup' ? 'topup' : item.burning ? 'burn' : item.balance > 0 ? 'accrual' : 'writeoff'
    const meta = {
        // Баллы из пуша — то же начисление, но с понятным источником
        accrual: { icon: <ArrowUpOutlined />, title: i.t(item.type === 'push' ? 'users:profile.pushAccrual' : 'users:profile.accrual') },
        writeoff: { icon: <ArrowDownOutlined />, title: i.t('users:profile.writeOffItem') },
        burn: { icon: <FireOutlined />, title: i.t('users:profile.burn') },
        topup: { icon: <WalletOutlined />, title: i.t('bonusProgram:topup.history') },
    }[kind]

    return (
        <div className={`user-history__item user-history__item--${kind}`}>
            <span className="user-history__icon">{meta.icon}</span>
            <div className="user-history__text">
                <div className="user-history__title">{meta.title}</div>
                <div className="user-history__sub">
                    {item.created_at && <span>{dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}</span>}
                    {item.order_id
                        ? <a href={`/shop/orders/${item.order_id}`}>#{item.order_id}</a>
                        : item.check_number && <span>{i.t('users:checkNumber', { number: item.check_number })}</span>
                    }
                </div>
            </div>
            <span className="user-history__amount">{item.balance > 0 ? '+' : ''}{item.balance}</span>
        </div>
    )
}

const BonusValidityItem = ({ item }: { item: any }) => (
    <div className="user-history__item user-history__item--validity">
        <span className="user-history__icon"><HourglassOutlined /></span>
        <div className="user-history__text">
            <div className="user-history__title">{i.t('def:accrued')}</div>
            {item.combustion_date &&
                <div className="user-history__sub">
                    <span>{i.t('users:profile.burnsAt', { date: dayjs(item.combustion_date).format('DD.MM.YYYY') })}</span>
                </div>
            }
        </div>
        <span className="user-history__amount">{item.balance}</span>
    </div>
)

const BonusCard = observer(() => {
    const [mode, setMode] = useState<'add' | 'remove'>('add')
    const readOnly = storeLocal.get('role') === 1
    const submit = () => mode === 'add' ? __users.addBonuses() : __users.deleteBonuses()

    return (
        <Card icon={<GiftOutlined />} title={i.t('def:bonuses')}>
            <div className="user-bonus">
                <div>
                    <div className="user-bonus__label">{i.t('users:profile.bonusBalance')}</div>
                    <div className="user-bonus__value">
                        {__users.bonus_balance ?? 0}
                        <span>{i.t('users:profile.bonusUnit')}</span>
                    </div>
                </div>
                <StarFilled className="user-bonus__star" />
            </div>

            <div className="user-bonus-form">
                <Segmented
                    block
                    value={mode}
                    onChange={value => setMode(value as 'add' | 'remove')}
                    options={[
                        { label: i.t('users:profile.accrue'), value: 'add', icon: <PlusOutlined /> },
                        { label: i.t('users:profile.writeOff'), value: 'remove', icon: <MinusOutlined /> },
                    ]}
                />
                <div className="user-bonus-form__row">
                    <InputNumber
                        variant="filled"
                        min={1}
                        className="user-bonus-form__amount"
                        placeholder={i.t('users:profile.amount')}
                        value={mode === 'add' ? __users.bonuses_add : __users.bonuses_delete}
                        onChange={value => {
                            if (mode === 'add') __users.bonuses_add = value ? value : 1
                            else __users.bonuses_delete = value ? value : 1
                        }}
                        // Enter не должен сохранять форму пользователя, в которой лежит карточка
                        onPressEnter={e => {
                            e.preventDefault()
                            if (!readOnly) submit()
                        }}
                    />
                    {mode === 'add' &&
                        <DatePicker
                            variant="filled"
                            format="DD.MM.YYYY"
                            className="user-bonus-form__date"
                            placeholder={i.t('def:combustion_date')}
                            value={__users.bonuses_add_combustion_date ? utcTimestampToDay(__users.bonuses_add_combustion_date) : null}
                            onChange={date => __users.bonuses_add_combustion_date = date ? dayToUtcTimestamp(date) : null}
                        />
                    }
                </div>
                <Button
                    block
                    type="primary"
                    danger={mode === 'remove'}
                    disabled={readOnly}
                    icon={mode === 'add' ? <PlusOutlined /> : <MinusOutlined />}
                    onClick={submit}
                >
                    {mode === 'add' ? i.t('users:profile.accrue') : i.t('users:profile.writeOff')}
                </Button>
            </div>
        </Card>
    )
})

const BonusHistoryPane = observer(() => {
    const items: any[] = __users.bonuses

    return (
        <div className="user-tabs__pane">
            <Segmented
                size="small"
                value={__users.bonus_table}
                onChange={value => __users.setTypeTable(value)}
                options={[
                    { label: i.t('def:detailing'), value: 1 },
                    { label: i.t('def:validity'), value: 2 },
                ]}
            />

            {items.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={i.t('users:profile.historyEmpty')} />
                : <div className="user-history">
                    {items.map((item: any, index: number) => __users.bonus_table === 1
                        ? <BonusHistoryItem key={item.id ?? index} item={item} />
                        : <BonusValidityItem key={item.id ?? index} item={item} />
                    )}
                </div>
            }

            {__users.bonuses_total > __users.bonuses_limit &&
                <Pagination
                    size="small"
                    className="user-history__pagination"
                    current={__users.bonuses_page}
                    total={__users.bonuses_total}
                    pageSize={__users.bonuses_limit}
                    showSizeChanger={false}
                    onChange={page => {
                        __users.bonuses_page = page
                        __users.getHistory(__users.bonus_table)
                    }}
                />
            }
        </div>
    )
})

const USER_REVIEWS_LIMIT = 5

const UserReviewItem = ({ review }: { review: any }) => {
    const history = useHistory()
    const link = entityLink(review)

    return (
        <div
            className="user-history__item user-review"
            onClick={event => {
                // Ссылка на товар или заказ ведёт туда, а не в отзыв
                if ((event.target as HTMLElement).closest('a')) return

                history.push(`/shop/reviews/${review.id}`)
            }}
        >
            <span className="user-history__icon">{typeIcon(review.entityType)}</span>
            <div className="user-history__text">
                <div className="user-history__title">
                    {i.t(`reviews:types.${review.entityType}`)}
                    {link && <> <a href={link} target="_blank" rel="noreferrer">#{review.entityId}</a></>}
                </div>
                <div className="user-history__sub">
                    <Link to={`/shop/reviews/${review.id}`}>{i.t('reviews:titleDetail')} #{review.id}</Link>
                    {review.createdAt && <span>{dayjs(review.createdAt).format('DD.MM.YYYY HH:mm')}</span>}
                </div>
                <div className={`user-review__comment${review.comment ? '' : ' user-review__comment--empty'}`}>
                    {review.comment ?? i.t('reviews:noComment')}
                </div>
            </div>
            <div className="user-review__side">
                <RatingStars value={review.rating} size={12} />
                <Tag color={statusColor(review.status)} style={{ margin: 0 }}>{i.t(`reviews:statuses.${review.status}`)}</Tag>
            </div>
        </div>
    )
}

/** Отзывы и оценки пользователя: что он писал о товарах, заказах и заведении. Грузим сразу, чтобы число было на вкладке */
const useUserReviews = (userId: number, enabled: boolean) => {
    const [reviews, setReviews] = useState<any[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)

    useEffect(() => {
        if (!enabled) return

        openApi('get', `/reviews?limit=${USER_REVIEWS_LIMIT}&page=${page}&sorting_type=id&sorting_action=desc&filter[user_id]=${userId}`)
            .then(result => {
                setReviews(result.data ?? [])
                setTotal(result.total ?? 0)
            })
            .catch(err => console.log(err.response))
    }, [userId, page, enabled])

    return { reviews, total, page, setPage }
}

const ReviewsPane = ({ userId, reviews, total, page, setPage }: ReturnType<typeof useUserReviews> & { userId: number }) => (
    <div className="user-tabs__pane">
        {reviews.length === 0
            ? <div className="user-card__empty">{i.t('users:profile.noReviews')}</div>
            : <div className="user-history">
                {reviews.map((review: any) => <UserReviewItem key={review.id} review={review} />)}
            </div>
        }
        {total > USER_REVIEWS_LIMIT &&
            <Pagination
                size="small"
                className="user-history__pagination"
                current={page}
                total={total}
                pageSize={USER_REVIEWS_LIMIT}
                showSizeChanger={false}
                onChange={setPage}
            />
        }
        {total > 0 && <Button block type="dashed" href={`/shop/reviews?user_id=${userId}`}>{i.t('users:profile.allReviews')}</Button>}
    </div>
)

const CompanyItem = ({ company, action, showVat }: { company: any, action: React.ReactNode, showVat?: boolean }) => (
    <div className="user-company">
        <div className="user-company__head">
            <span className="user-company__name">{company.type === 2 ? company.name : company.fio}</span>
            {action}
        </div>
        <Space size={4} wrap>
            <Tag bordered={false}>{company.type === 2 ? i.t('def:Legal entity') : i.t('def:Individual entrepreneur')}</Tag>
            <Tag bordered={false} color={company.status ? 'success' : 'error'}>
                {company.status ? i.t('order:companyActive') : i.t('order:companyInactive')}
            </Tag>
        </Space>
        <div className="user-company__meta">
            <span>{i.t('order:innOgrn', { inn: company.inn, ogrn: company.ogrn })}</span>
            {company.kpp && <span>{i.t('order:kppLine', { kpp: company.kpp })}</span>}
            {showVat && <span>{i.t('users:vatLine', { value: company.nds ? i.t('def:yes') : i.t('def:no') })}</span>}
            {company.address && <span>{i.t('order:legalAddressLine', { address: company.address })}</span>}
        </div>
    </div>
)

const CompaniesPane = observer(() => (
    <div className="user-tabs__pane">
        {__users.user.companies.length === 0
            ? <div className="user-card__empty">{i.t('users:profile.noCompanies')}</div>
            : <div className="user-companies">
                {__users.user.companies.map((company: any) => (
                    <CompanyItem
                        key={company.id}
                        company={company}
                        showVat
                        action={<Button size="small" type="text" danger onClick={() => __users.deleteCompanyFromUser(company.id)}>{i.t('def:actions.remove')}</Button>}
                    />
                ))}
            </div>
        }
        <Button block type="dashed" icon={<PlusOutlined />} onClick={() => __users.openSearchCompany()}>{i.t('def:actions.add')}</Button>

        <Modal open={__users.modalCompany.open} title={i.t('def:Add company')} onCancel={() => __users.closeSearchCompany()} footer={false}>
            <div className="user-companies">
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        value={__users.modalCompany.inn ?? ''}
                        onChange={(e: any) => __users.modalCompany.inn = e.target.value}
                        onPressEnter={() => __users.searchCompany()}
                    />
                    <Button type="primary" onClick={() => __users.searchCompany()}>{i.t('def:actions.search')}</Button>
                </Space.Compact>
                {__users.modalCompany.list.map((company: any, index: number) => (
                    <CompanyItem
                        key={company.inn ?? index}
                        company={company}
                        action={<Button size="small" type="dashed" onClick={() => __users.addCompanyToUser(company)}>{i.t('def:actions.select')}</Button>}
                    />
                ))}
            </div>
        </Modal>
    </div>
))

// Число рядом с названием вкладки; иконок и кнопок в строке вкладок нет — иначе три вкладки не влезают в колонку
const TabLabel = ({ title, count }: { title: React.ReactNode, count?: number }) => (
    <span className="user-tabs__label">
        {title}
        {count ? <span className="user-tabs__count">{count}</span> : null}
    </span>
)

/** История бонусов, юрлица и отзывы — вкладками, а не друг под другом: иначе правая колонка уходила в длинную ленту */
const UserTabsCard = observer(({ userId }: { userId: number }) => {
    const showReviews = planAllows(REVIEW_PLANS.order)
    const reviews = useUserReviews(userId, showReviews)

    const items = [
        { key: 'history', label: <TabLabel title={i.t('users:profile.bonusHistoryTab')} />, children: <BonusHistoryPane /> },
        {
            key: 'companies',
            label: <TabLabel title={i.t('users:profile.companiesTab')} count={__users.user.companies.length} />,
            children: <CompaniesPane />,
        },
    ]
    if (showReviews) {
        items.push({
            key: 'reviews',
            label: <TabLabel title={i.t('reviews:title')} count={reviews.total} />,
            children: <ReviewsPane userId={userId} {...reviews} />,
        })
    }

    return (
        <section className="user-card user-tabs">
            <Tabs tabBarGutter={20} items={items} />
        </section>
    )
})

export const UserDetail = observer(() => {
    let { userID }: any = useParams()

    useEffect(() => {
        projectTime.load()
        __users.getOne(userID)
        price_types_controller.get()
    }, [])

    const user: any = __users.user
    const isNew = !(user.id > 0)
    const hasFields = Array.isArray(user.fields) && user.fields.length > 0

    return (
        <form className="user-page" onSubmit={e => __users.save(e)}>
            <Titles
                title={i.t('user:info')}
                buttons={[
                    { title: i.t('def:actions.save'), type: 'primary', htmlType: 'submit' },
                    { title: i.t('def:actions.backList'), type: 'dashed', href: '/users' },
                ]}
                breadcrumb={[
                    { title: i.t('users:listUsers'), href: '/users' },
                    { title: i.t('user:info') },
                ]}
            />

            {user.deleted_at &&
                <Alert
                    type="error"
                    showIcon
                    message={i.t('users:deletedTitle')}
                    description={i.t('users:deletedDescription', { date: dayjs(user.deleted_at).format('DD.MM.YYYY HH:mm') })}
                />
            }

            <UserHero />

            {!isNew && <UserStats />}

            <Row gutter={[20, 20]}>
                <Col xs={24} xl={isNew ? 24 : 13}>
                    <div className="user-column">
                        <PersonalCard />
                        <SecurityCard />
                        {hasFields &&
                            <Card icon={<ProfileOutlined />} title={i.t('order:additionalFields')}>
                                <Row gutter={[15, 15]}>
                                    <AdditionalFields fields={user.fields} />
                                </Row>
                            </Card>
                        }
                        {!isNew && <BonusCard />}
                    </div>
                </Col>
                {!isNew &&
                    <Col xs={24} xl={11}>
                        <UserTabsCard userId={user.id} />
                    </Col>
                }
            </Row>
        </form>
    )
})
